import { inject, Injectable, signal } from '@angular/core';
import { FirstTouchModule } from '@app/+authenticated/shared/first-touch/first-touch.model';
import { OnboardingImportService } from '@app/+onboarding/import/onboarding-import.service';
import { ScheduleAction } from '@app/reducers/orm/schedule/schedule.action';
import { ScheduleService } from '@app/reducers/orm/schedule/schedule.service';
import { UserGuideStatus, UserGuideType } from '@app/reducers/orm/user-guide/user-guide.model';
import { selectUserGuideByType } from '@app/reducers/orm/user-guide/user-guide.selector';
import { UserGuideService } from '@app/reducers/orm/user-guide/user-guide.service';
import { SentryLoggingService } from '@app/services/sentryLogging.service';
import { TrackingService } from '@app/services/tracking.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { selectScheduleNDraftState, selectTeamDataDraftState } from '@reducers/store/schedule/auto-schedule.selector';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { ToastService } from '@sb/ui/toast';
import { asapScheduler, firstValueFrom, observeOn, Subscription, take } from 'rxjs';

import { OnboardingDraftScheduleAiService } from './onboarding-draft-schedule-ai.service';
import {
  type DraftSchedulePeriod,
  type DraftScheduleSummary,
  type ReconciledEntry,
} from './onboarding-draft-schedule.models';
import { buildProposedSchedules, buildRosterPayloads } from './onboarding-draft-schedule.payloads';
import {
  dedupeEmployeeOverlaps,
  partitionRecommendation,
  reconcileRecommendation,
  summarizeDraft,
} from './onboarding-draft-schedule.reconciler';
import { readReconciliationSource } from './onboarding-draft-schedule.source';
import { timeoutTo } from './onboarding-draft-schedule.timeout';

export type ScheduleViewMode = 'employee' | 'team';

// Upper bound on the LLM 3rd-pass reconciliation. Past this we fall back to the
// deterministic matcher (or an empty draft), so a stalled proxy can't hang whenReady().
export const LLM_RECONCILE_TIMEOUT_MS = 8000;

/**
 * Orchestrates the post-onboarding "here's the schedule we inferred" moment.
 *
 * Reconciliation (an LLM "3rd pass", falling back to deterministic name matching)
 * runs during the onboarding celebration screen via `prepareDraft()` — a head
 * start while the user reads it — so by the time they click through, the entries
 * are ready. `maybeStart(mode, period)` then drafts those entries onto the grid
 * as a non-persisted overlay (reusing the auto-schedule proposal channel) and
 * drives the accept panel; nothing is written until the user accepts.
 *
 * The overlay is REACTIVE: `maybeStart` runs on every view/week change and
 * re-dispatches for the active view+week, so switching view or paging keeps it visible.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingDraftScheduleService {
  #store = inject<Store<AppState>>(Store);
  #scheduleService = inject(ScheduleService);
  #importService = inject(OnboardingImportService);
  #featureService = inject(FeatureService);
  #aiReconciler = inject(OnboardingDraftScheduleAiService);
  #userGuideService = inject(UserGuideService);
  #toast = inject(ToastService);
  #translate = inject(TranslateService);
  #sentryLogging = inject(SentryLoggingService);
  #tracking = inject(TrackingService);

  #isActive = signal(false);
  isActive = this.#isActive.asReadonly();

  // True while the reconciliation runs (celebration head-start, or arrival fallback).
  #preparing = signal(false);
  isPreparing = this.#preparing.asReadonly();

  // Panel shows a loading state only on the arrival-fallback path (hard reload).
  #isLoading = signal(false);
  isLoading = this.#isLoading.asReadonly();

  #summary = signal<DraftScheduleSummary | null>(null);
  summary = this.#summary.asReadonly();

  #accepting = signal(false);
  isAccepting = this.#accepting.asReadonly();

  // Reconciled entries (null until reconciliation resolves) + the currently
  // viewed mode/week the overlay is dispatched for.
  #entries = signal<ReconciledEntry[] | null>(null);
  #mode: ScheduleViewMode = 'employee';
  #period: DraftSchedulePeriod | null = null;

  // The in-flight (or resolved) reconciliation, so the finish flow and the
  // schedule arrival can both await the same head-started pass.
  #readyPromise: Promise<void> | null = null;

  // One in-flight "wait for this view's store period" subscription at a time.
  #pendingDispatch: Subscription | null = null;

  // Guards the arrival-side activation so it happens once.
  #started = false;

  // Bumped on every #finish() so an in-flight reconciliation that resolves after a
  // dismiss knows it was cancelled and skips writing #entries (else it resurrects).
  #generation = 0;

  // Tracking: when preparation began — draft_build_ms measures the user-experienced
  // time from here until the draft is shown (celebration dwell included), not the
  // request duration. #startedTracked gates the once-only "Schedule Started" event.
  #buildStartedAt = 0;
  #startedTracked = false;

  // Drafted departments locked (dispatched once on activation, cleared on finish).
  #departmentsLocked = false;

  /** Whether there is a stashed recommendation to draft (flag-gated). */
  hasPendingRecommendation(): boolean {
    return (
      this.#featureService.isEnabled(featureFlag.TMP_AI_ONBOARDING) && !!this.#importService.recommendation()?.length
    );
  }

  /** Whether reconciliation has produced at least one usable entry. */
  hasEntries(): boolean {
    return !!this.#entries()?.length;
  }

  /** Resolves once the reconciliation head-start (if any) has settled. Never rejects. */
  whenReady(): Promise<void> {
    return this.#readyPromise ?? Promise.resolve();
  }

  /**
   * Kick off the LLM reconciliation for the created teams/shifts/employees.
   * Called when the onboarding celebration screen appears so it runs while the
   * user reads it. Idempotent and best-effort.
   */
  prepareDraft(): void {
    if (this.#readyPromise || !this.hasPendingRecommendation()) {
      return;
    }
    // Stamp the build start for draft_build_ms. (First-touch is now marked from
    // #reconcile once entries materialise, not here.)
    this.#buildStartedAt = Date.now();
    this.#readyPromise = this.#reconcile();
  }

  async #reconcile(): Promise<void> {
    const recommendation = this.#importService.recommendation();
    if (!recommendation?.length) {
      return;
    }

    // Snapshot the generation: if #finish() runs while the async passes below are
    // in flight, this no longer matches and we drop the resolved entries on the
    // floor rather than repopulating a draft the user already dismissed.
    const generation = this.#generation;

    this.#preparing.set(true);
    try {
      const source = readReconciliationSource(this.#store);

      // Exact team+shift matches bind deterministically here so the LLM can never
      // substitute a different template when an exact-name match exists. Only the
      // leftovers (no exact match) go to the 3rd pass for fuzzy resolution.
      const { bound, leftover } = partitionRecommendation(recommendation, source);

      let resolved = bound;
      if (leftover.length > 0) {
        // Bound the LLM pass: on timeout resolve to [] so whenReady() (and the
        // celebration CTA gated on it) can never hang on a stalled proxy.
        const reconcileTimeout = timeoutTo<ReconciledEntry[]>(LLM_RECONCILE_TIMEOUT_MS, []);

        let llmBound: ReconciledEntry[];
        try {
          llmBound = await Promise.race([this.#aiReconciler.reconcile(leftover, source), reconcileTimeout.promise]);
        } finally {
          // Clear the timer whichever side won the race, so it can't fire and
          // resolve into the void after we've already moved on.
          reconcileTimeout.cancel();
        }

        if (llmBound.length === 0) {
          // 3rd pass errored/returned nothing — the deterministic matcher can't
          // bind these non-exact leftovers, but call it so any exact match would
          // still be honoured were the partitioning ever to change.
          llmBound = reconcileRecommendation(leftover, source);
        }
        resolved = [...bound, ...llmBound];
      }

      // A dismiss/#finish() during the async passes bumps the generation; if that
      // happened, drop the result instead of resurrecting the discarded draft.
      if (generation !== this.#generation) {
        return;
      }

      // Guarantee no employee is double-booked on a weekday, whichever path ran,
      // before the proposals/rosters are built from these entries.
      const deduped = dedupeEmployeeOverlaps(resolved);
      this.#entries.set(deduped);

      // Consume the schedule first-touch only once a draft will actually show
      // (still during the celebration head-start on the onboarding path); leave it
      // intact when reconciliation yields nothing.
      if (deduped.length > 0) {
        this.#markScheduleFirstTouch();
      }
    } catch {
      // Best-effort — never block the finish flow. Leave entries empty so the
      // caller falls back to the normal aha-moment.
    } finally {
      this.#preparing.set(false);
    }
  }

  /**
   * Called on every (mode, period) change from the schedule page. Dispatches the
   * already-reconciled overlay for the active view; on a hard reload (singleton
   * reset, so no head-start ran) it reconciles from the stashed recommendation
   * on arrival, showing the panel loading state until ready.
   */
  maybeStart(mode: ScheduleViewMode, period: DraftSchedulePeriod): void {
    // accept() owns the lifecycle once it starts — a re-mount must not re-activate
    // and re-dispatch the overlay accept() already cleared (destroy guard skips it).
    if (this.#accepting()) {
      return;
    }

    if (!period.minDate || (!this.hasPendingRecommendation() && !this.hasEntries())) {
      return;
    }

    this.#mode = mode;
    this.#period = period;

    if (this.hasEntries()) {
      this.#isActive.set(true);
      this.#dispatchForCurrentView();
      return;
    }

    if (this.#started) {
      return;
    }
    this.#started = true;
    this.#isActive.set(true);
    this.#isLoading.set(true);
    this.prepareDraft();
    this.#settleWhenReady();
  }

  #settleWhenReady(): void {
    this.whenReady()
      .then(() => {
        this.#isLoading.set(false);
        if (this.hasEntries()) {
          this.#dispatchForCurrentView();
        } else {
          this.#finish();
        }
      })
      .catch(() => this.#finish());
  }

  #dispatchForCurrentView(): void {
    const entries = this.#entries();
    const period = this.#period;
    if (!entries?.length || !period) {
      return;
    }

    const mode = this.#mode;

    // Lock the drafted departments once, reusing the auto-schedule lock machinery:
    // schedule interactions are disabled until the user accepts/dismisses. The
    // reducer treats this as a no-op for the proposal entities (it only flips the
    // flag), and a schedule LOAD preserves it, so dispatching once is enough.
    if (!this.#departmentsLocked) {
      const lockedDepartmentIds = [...new Set(entries.map((entry) => entry.departmentId).filter((id) => !!id))];
      this.#store.dispatch(ScheduleAction.setAutoScheduleLockedDepartments(lockedDepartmentIds));
      this.#departmentsLocked = true;
    }

    // Self-healing overlay: instead of enumerating the many actions that can wipe
    // the in-range proposals (schedule/scheduleN/teamData LOAD_SUCCESS, RESET,
    // filter reprocesses, …), watch the store the grid actually reads for the
    // current view and RE-ASSERT the overlay whenever our proposals are absent.
    // observeOn(asapScheduler) defers to a microtask so the clobbering reducer has
    // settled before we check. This converges (once proposals are present nothing
    // is dispatched) and can't loop, because dispatching emits
    // SET_AUTO_SCHEDULES_PROPOSALS, which makes them present.
    const draftState$ = this.#store.select(mode === 'team' ? selectTeamDataDraftState : selectScheduleNDraftState);

    this.#pendingDispatch?.unsubscribe();
    this.#pendingDispatch = draftState$.pipe(observeOn(asapScheduler)).subscribe(({ minDate, hasProposals }) => {
      // Gate on the view's period being ready; skip while the overlay is present.
      if (minDate !== period.minDate || hasProposals) {
        return;
      }
      const proposed = buildProposedSchedules(entries, period);
      this.#store.dispatch(ScheduleAction.setAutoSchedulesProposals(proposed));

      const summary = summarizeDraft(entries, proposed.length);
      this.#summary.set(summary);

      // Fire once, when the draft is first shown with at least one entry.
      if (!this.#startedTracked) {
        this.#startedTracked = true;
        void this.#tracking.event('Signup Aha Moment Schedule Started', {
          shifts_scheduled: summary.occurrenceCount,
          recurring_shifts: summary.entryCount,
          shifts_assigned: summary.assignedCount,
          // Deliberately the user-experienced wait (start-of-prep → draft shown),
          // not the request duration — includes celebration dwell on the normal path.
          draft_build_ms: this.#buildStartedAt ? Date.now() - this.#buildStartedAt : 0,
        });
      }
    });
  }

  /** Persist the drafted shifts as recurring rosters, then clear the overlay + stash. */
  async accept(): Promise<void> {
    if (this.#accepting()) {
      return;
    }

    const entries = this.#entries();
    const period = this.#period;

    if (!entries?.length || !period) {
      this.dismiss();
      return;
    }

    this.#accepting.set(true);
    this.#pendingDispatch?.unsubscribe();

    // Snapshot the summary before the await loop so a racing teardown can't null
    // it out from under the Accepted event (which would then report zero counts).
    const summary = this.#summary();

    // finally releases the accepting flag even if a dispatch/toast/#finish throws —
    // otherwise the panel's disabled close/Dismiss + sbLadda strand the user.
    try {
      const payloads = buildRosterPayloads(entries, period);
      const [firstPayload, ...restPayloads] = payloads;

      // A recurring entry always yields a payload; this keeps the tuple non-empty.
      if (!firstPayload) {
        this.#finish();
        return;
      }

      // One batch request, not a POST-per-roster loop that tripped the rate limiter.
      let createdFailed = 0;
      try {
        await firstValueFrom(this.#scheduleService.addBatch([firstPayload, ...restPayloads]));
      } catch (error) {
        createdFailed = payloads.length;
        this.#sentryLogging.handleError(error);
        this.#toast.openDanger(this.#translate.instant('We could not save your draft schedule. Please try again.'));
      }

      void this.#tracking.event('Signup Aha Moment Schedule Accepted', {
        shifts_scheduled: summary?.occurrenceCount ?? 0,
        recurring_shifts: summary?.entryCount ?? payloads.length,
        shifts_assigned: summary?.assignedCount ?? 0,
        created_ok: payloads.length - createdFailed,
        created_failed: createdFailed,
      });

      // Tear down only on success (real rosters arrive via ADD_SUCCESS); a failed batch keeps the draft.
      if (createdFailed === 0) {
        this.#store.dispatch(ScheduleAction.clearAutoSchedules());
        this.#finish();
      }
    } finally {
      this.#accepting.set(false);
    }
  }

  /** Discard the draft overlay and stash without persisting anything. */
  dismiss(reason: 'button' | 'navigated_away' = 'button'): void {
    // accept() owns teardown; a dismiss racing an in-flight accept (navigate-away
    // mid-persist) must no-op, not fire a second outcome event or clear its state.
    if (this.#accepting()) {
      return;
    }

    // Gate on the same condition as Started so every Dismissed has a matching
    // Started (entries can exist before the overlay is dispatched).
    if (this.#startedTracked) {
      void this.#tracking.event('Signup Aha Moment Schedule Dismissed', {
        dismiss_reason: reason,
        shifts_scheduled: this.#summary()?.occurrenceCount ?? 0,
      });
    }

    this.#pendingDispatch?.unsubscribe();
    this.#store.dispatch(ScheduleAction.clearAutoSchedules());
    this.#finish();
  }

  #finish(): void {
    // Invalidate any in-flight reconciliation so its result can't repopulate the
    // entries we're about to clear.
    this.#generation += 1;
    this.#pendingDispatch?.unsubscribe();
    // Release the department lock so the persisted shifts aren't left locked after
    // accept, and normal interaction is restored after dismiss. (accept()/dismiss()
    // both reach here; clearAutoSchedules also resets it, this is the explicit clear.)
    if (this.#departmentsLocked) {
      this.#store.dispatch(ScheduleAction.setAutoScheduleLockedDepartments([]));
      this.#departmentsLocked = false;
    }
    this.#importService.clearScheduleRecommendation();
    this.#entries.set(null);
    this.#period = null;
    this.#summary.set(null);
    this.#isLoading.set(false);
    this.#isActive.set(false);
    this.#startedTracked = false;
    this.#buildStartedAt = 0;
  }

  // Record the schedule module as touched via the low-level user-guide service
  // (FirstTouchService can't be used here — it imports @app/+onboarding, which
  // would be a circular dependency). Merges into the existing touched list so it
  // never clobbers other modules, and records SCHEDULE even if the guide hasn't
  // loaded yet.
  #markScheduleFirstTouch(): void {
    const guide = this.#store.selectSignal(selectUserGuideByType(UserGuideType.TOUCHED_MODULES))();
    const touched = guide?.step ? this.#userGuideService.getListOfSteps(guide.step) : [];

    if (touched.includes(FirstTouchModule.SCHEDULE)) {
      return;
    }

    this.#userGuideService
      .trackUserGuide({
        guide: UserGuideType.TOUCHED_MODULES,
        step: this.#userGuideService.mergeListOfSteps([...touched, FirstTouchModule.SCHEDULE]),
        status: UserGuideStatus.IN_PROGRESS,
      })
      .pipe(take(1))
      .subscribe();
  }
}
